// src/components/AutomationsPage.tsx
// Módulo de automatizaciones — cron/event/manual/webhook triggers con modelo de IA.
// Design system: Orion glass-morphism (o-card, o-btn-ghost, --acc1/2/3, white/N).

import React, { useEffect, useState, useCallback } from 'react';
import {
  listAutomations, createAutomation, updateAutomation, deleteAutomation,
  runAutomation, listAutomationRuns, getMCPTools,
  type Automation, type AutomationRun, type AutomationStatus, type AutomationTrigger,
  type MCPTool,
} from '@/lib/api';

import {
  Loader, Plus, Pencil, Trash2, X, Save, Play, Clock, Zap,
  ChevronDown, ChevronRight, AlertCircle, CheckCircle2,
  RotateCw, Search, FileText, Settings, Wrench,
} from 'lucide-react';

/* ═══════════════════════════════════════════════════════════════
   UI Primitives (local copies — same as TravelAdmin)
   ═══════════════════════════════════════════════════════════════ */

function Badge({ children, variant = 'default' }: {
  children: React.ReactNode;
  variant?: 'success' | 'error' | 'warn' | 'info' | 'default' | 'accent';
}) {
  const s: Record<string, string> = {
    success: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/25',
    error:   'bg-red-500/15 text-red-400 border-red-500/25',
    warn:    'bg-amber-500/15 text-amber-400 border-amber-500/25',
    info:    'bg-sky-500/15 text-sky-400 border-sky-500/25',
    accent:  'bg-[rgba(var(--acc2-rgb),.15)] text-[var(--acc2)] border-[rgba(var(--acc2-rgb),.25)]',
    default: 'bg-white/10 text-white/50 border-white/10',
  };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium border ${s[variant]}`}>{children}</span>;
}

function EmptyState({ icon, text }: { icon: React.ReactNode; text: string }) {
  return <div className="text-center py-14 text-white/20"><div className="mx-auto mb-3 opacity-40">{icon}</div><p>{text}</p></div>;
}

function Field({ label, children, hint }: { label: string; children: React.ReactNode; hint?: string }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-widest text-white/30 font-semibold">{label}</span>
      {hint && <span className="text-[10px] text-white/20 ml-2">({hint})</span>}
      <div className="mt-1">{children}</div>
    </label>
  );
}

function Input({ value, onChange, placeholder, type = 'text', ...rest }: React.InputHTMLAttributes<HTMLInputElement> & { value: string | number; onChange: (e: React.ChangeEvent<HTMLInputElement>) => void }) {
  return (
    <input type={type} value={value} onChange={onChange} placeholder={placeholder} {...rest}
      className="w-full px-3 py-2 text-sm rounded-lg bg-white/5 border border-white/10 text-white placeholder:text-white/20 focus:ring-1 focus:ring-[var(--acc1)] focus:border-[var(--acc1)] outline-none transition-colors" />
  );
}

function Textarea({ value, onChange, rows = 4, placeholder, ...rest }: React.TextareaHTMLAttributes<HTMLTextAreaElement> & { value: string; onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void }) {
  return (
    <textarea value={value} onChange={onChange} rows={rows} placeholder={placeholder} {...rest}
      className="w-full px-3 py-2 text-sm rounded-lg bg-white/5 border border-white/10 text-white placeholder:text-white/20 focus:ring-1 focus:ring-[var(--acc1)] focus:border-[var(--acc1)] outline-none transition-colors font-mono resize-y" />
  );
}

function SaveBtn({ onClick, saving, label = 'Guardar', disabled }: { onClick: () => void; saving: boolean; label?: string; disabled?: boolean }) {
  return (
    <button onClick={onClick} disabled={saving || disabled}
      className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold transition-all disabled:opacity-40"
      style={{ background: 'var(--acc1)', color: 'var(--bg-dark)' }}>
      {saving ? <Loader size={14} className="animate-spin" /> : <Save size={14} />}
      {saving ? 'Guardando…' : label}
    </button>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Constants
   ═══════════════════════════════════════════════════════════════ */

const STATUS_BADGE: Record<string, 'success' | 'error' | 'warn' | 'default'> = {
  active: 'success', paused: 'default', error: 'error', draft: 'warn',
};
const STATUS_LABEL: Record<string, string> = {
  active: 'Activa', paused: 'Pausada', error: 'Error', draft: 'Borrador',
};

const RUN_BADGE: Record<string, 'success' | 'error' | 'warn' | 'info' | 'default'> = {
  running: 'info', success: 'success', partial: 'warn', failed: 'error',
};
const RUN_LABEL: Record<string, string> = {
  running: 'Ejecutando', success: 'Éxito', partial: 'Parcial', failed: 'Falló',
};

const TRIGGER_ICON: Record<string, React.ReactNode> = {
  cron: <Clock size={14} />,
  event: <Zap size={14} />,
  webhook: <RotateCw size={14} />,
  manual: <Play size={14} />,
};
const TRIGGER_LABEL: Record<string, string> = {
  cron: 'Programada (Cron)', event: 'Evento', webhook: 'Webhook', manual: 'Manual',
};

const MODELS = [
  { value: '', label: 'Auto (AI Router decide)' },
  { value: 'gpt-5.2', label: 'GPT-5.2' },
  { value: 'gpt-5.2-codex', label: 'GPT-5.2 Codex' },
  { value: 'gpt-5.2-pro', label: 'GPT-5.2 Pro' },
  { value: 'gpt-5-nano', label: 'GPT-5 Nano' },
];

/* Common cron presets */
const CRON_PRESETS: { label: string; value: string }[] = [
  { label: 'Cada hora', value: '0 * * * *' },
  { label: 'Cada 6 horas', value: '0 */6 * * *' },
  { label: 'Diario 9am', value: '0 9 * * *' },
  { label: 'Cada 3 días 9am', value: '0 9 */3 * *' },
  { label: 'Semanal (lunes 8am)', value: '0 8 * * 1' },
  { label: 'Mensual (día 1, 9am)', value: '0 9 1 * *' },
];

/* ═══════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════════ */

export function AutomationsPage({ addNotification }: { addNotification?: (n: any) => void }) {
  const [automations, setAutomations] = useState<Automation[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [searchQ, setSearchQ] = useState('');

  // Modal states
  const [showEditor, setShowEditor] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState<string | null>(null); // automation id

  const notify = useCallback((type: 'success' | 'error' | 'info', message: string) => {
    if (addNotification) addNotification({ type, message });
    else console.log(`[${type}]`, message);
  }, [addNotification]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listAutomations(filterStatus ? { status: filterStatus } : undefined);
      setAutomations(data.automations || []);
    } catch (e: any) { notify('error', e.message); }
    finally { setLoading(false); }
  }, [filterStatus, notify]);

  useEffect(() => { load(); }, [load]);

  const filtered = automations.filter(a => {
    if (searchQ.trim()) {
      const q = searchQ.toLowerCase();
      return a.name.toLowerCase().includes(q) || a.description?.toLowerCase().includes(q);
    }
    return true;
  });

  const handleToggleStatus = async (a: Automation) => {
    const newStatus: AutomationStatus = a.status === 'active' ? 'paused' : 'active';
    try {
      await updateAutomation(a.id, { status: newStatus });
      notify('success', `Automatización ${newStatus === 'active' ? 'activada' : 'pausada'}`);
      load();
    } catch (e: any) { notify('error', e.message); }
  };

  const handleDelete = async (a: Automation) => {
    if (!confirm(`¿Eliminar "${a.name}"?`)) return;
    try {
      await deleteAutomation(a.id);
      notify('success', 'Automatización eliminada');
      load();
    } catch (e: any) { notify('error', e.message); }
  };

  const handleRun = async (a: Automation) => {
    if (!confirm(`¿Ejecutar "${a.name}" ahora?`)) return;
    try {
      await runAutomation(a.id);
      notify('success', 'Ejecución iniciada');
      load();
    } catch (e: any) { notify('error', e.message); }
  };

  const openNew = () => { setEditingId(null); setShowEditor(true); };
  const openEdit = (id: string) => { setEditingId(id); setShowEditor(true); };

  return (
    <div className="space-y-5 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-white flex items-center gap-2">
            <Zap size={20} style={{ color: 'var(--acc2)' }} />
            Automatizaciones
          </h1>
          <p className="text-xs text-white/30 mt-1">Tareas programadas y reactivas ejecutadas por el agente de IA</p>
        </div>
        <button onClick={openNew}
          className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-all"
          style={{ background: 'var(--acc1)', color: 'var(--bg-dark)' }}>
          <Plus size={14} />Nueva automatización
        </button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
          <input type="text" placeholder="Buscar por nombre…" value={searchQ} onChange={e => setSearchQ(e.target.value)}
            className="w-full pl-8 pr-3 py-2 text-sm rounded-lg bg-transparent border border-white/10 text-white placeholder:text-white/30 focus:ring-1 focus:ring-[var(--acc1)] outline-none" />
        </div>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          className="px-3 py-2 text-sm rounded-lg bg-transparent border border-white/10 text-white outline-none appearance-none cursor-pointer">
          <option value="" className="bg-[var(--bg-dark)]">Todos los estados</option>
          {['active', 'paused', 'error', 'draft'].map(s => (
            <option key={s} value={s} className="bg-[var(--bg-dark)]">{STATUS_LABEL[s]}</option>
          ))}
        </select>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center gap-2 py-12 justify-center text-white/30 text-sm">
          <Loader size={16} className="animate-spin" />Cargando…
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState icon={<Zap size={32} />} text="Sin automatizaciones" />
      ) : (
        <div className="space-y-3">
          {filtered.map(a => (
            <AutomationCard
              key={a.id}
              automation={a}
              onToggle={() => handleToggleStatus(a)}
              onEdit={() => openEdit(a.id)}
              onDelete={() => handleDelete(a)}
              onRun={() => handleRun(a)}
              onHistory={() => setShowHistory(a.id)}
            />
          ))}
        </div>
      )}

      {/* Editor modal */}
      {showEditor && (
        <AutomationEditor
          automationId={editingId}
          onClose={() => setShowEditor(false)}
          onSaved={() => { setShowEditor(false); load(); }}
          notify={notify}
        />
      )}

      {/* History drawer */}
      {showHistory && (
        <HistoryDrawer
          automationId={showHistory}
          automationName={automations.find(a => a.id === showHistory)?.name || ''}
          onClose={() => setShowHistory(null)}
          notify={notify}
        />
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   AUTOMATION CARD
   ═══════════════════════════════════════════════════════════════ */

function AutomationCard({ automation: a, onToggle, onEdit, onDelete, onRun, onHistory }: {
  automation: Automation;
  onToggle: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onRun: () => void;
  onHistory: () => void;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="o-card overflow-hidden">
      {/* Header row */}
      <div className="flex items-center gap-3">
        {/* Toggle */}
        <button onClick={onToggle} title={a.status === 'active' ? 'Pausar' : 'Activar'}
          className={`w-10 h-5 rounded-full relative transition-colors shrink-0 ${a.status === 'active' ? 'bg-emerald-500/40' : 'bg-white/10'}`}>
          <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${a.status === 'active' ? 'translate-x-5' : 'translate-x-0.5'}`} />
        </button>

        {/* Info */}
        <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setExpanded(!expanded)}>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-white font-semibold text-sm">{a.name}</span>
            <Badge variant={STATUS_BADGE[a.status] || 'default'}>{STATUS_LABEL[a.status] || a.status}</Badge>
            <span className="text-[10px] text-white/20 flex items-center gap-1">{TRIGGER_ICON[a.trigger_type]}{TRIGGER_LABEL[a.trigger_type]}</span>
          </div>
          {a.description && <p className="text-[11px] text-white/30 mt-0.5 truncate">{a.description}</p>}
        </div>

        {/* Stats */}
        <div className="hidden sm:flex items-center gap-4 shrink-0 text-[10px] text-white/25">
          {a.last_run_at && (
            <span title="Última ejecución">
              <Clock size={10} className="inline mr-1" />
              {new Date(a.last_run_at).toLocaleDateString('es', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
          <span title="Ejecuciones">{a.run_count} runs</span>
          {a.error_count > 0 && <span className="text-red-400" title="Errores">{a.error_count} errores</span>}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 shrink-0">
          <button onClick={onRun} title="Ejecutar ahora" className="p-1.5 rounded-lg text-white/30 hover:text-emerald-400 hover:bg-emerald-500/10 transition-colors">
            <Play size={14} />
          </button>
          <button onClick={onHistory} title="Historial" className="p-1.5 rounded-lg text-white/30 hover:text-white/60 hover:bg-white/5 transition-colors">
            <FileText size={14} />
          </button>
          <button onClick={onEdit} title="Editar" className="p-1.5 rounded-lg text-white/30 hover:text-white/60 hover:bg-white/5 transition-colors">
            <Pencil size={14} />
          </button>
          <button onClick={onDelete} title="Eliminar" className="p-1.5 rounded-lg text-white/30 hover:text-red-400 hover:bg-red-500/10 transition-colors">
            <Trash2 size={14} />
          </button>
        </div>

        {/* Expand */}
        <button onClick={() => setExpanded(!expanded)} className="p-1 text-white/20 hover:text-white/40 transition-colors">
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
      </div>

      {/* Expanded details */}
      {expanded && (
        <div className="mt-3 pt-3 border-t border-white/[.06] space-y-2">
          {a.cron_expression && (
            <div className="flex items-center gap-2 text-xs">
              <span className="text-white/30">Cron:</span>
              <code className="px-2 py-0.5 rounded bg-white/5 text-white/50 font-mono text-[11px]">{a.cron_expression}</code>
            </div>
          )}
          {a.event_type && (
            <div className="flex items-center gap-2 text-xs">
              <span className="text-white/30">Evento:</span>
              <code className="px-2 py-0.5 rounded bg-white/5 text-white/50 font-mono text-[11px]">{a.event_type}</code>
            </div>
          )}
          <div className="text-xs">
            <span className="text-white/30">Modelo:</span>
            <span className="ml-2 text-white/50">{a.model || 'Auto (AI Router)'}</span>
            <span className="text-white/20 ml-3">Máx iteraciones: {a.max_iterations}</span>
          </div>
          {a.tools_allowed.length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap text-[10px]">
              <span className="text-white/30">Tools:</span>
              {a.tools_allowed.map((t: string) => (
                <span key={t} className="px-1.5 py-0.5 rounded bg-white/5 text-white/40 border border-white/5">{t}</span>
              ))}
            </div>
          )}
          <div className="text-xs">
            <span className="text-white/30">Prompt template:</span>
            <pre className="mt-1 p-2 rounded-lg bg-white/[.03] border border-white/[.06] text-white/40 text-[11px] font-mono whitespace-pre-wrap max-h-[150px] overflow-y-auto">
              {a.prompt_template}
            </pre>
          </div>
          {a.last_run_summary && (
            <div className="text-xs">
              <span className="text-white/30">Último resumen:</span>
              <p className="mt-1 text-white/40 text-[11px]">{a.last_run_summary}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   EDITOR MODAL (Create / Edit)
   ═══════════════════════════════════════════════════════════════ */

function AutomationEditor({ automationId, onClose, onSaved, notify }: {
  automationId: string | null;
  onClose: () => void;
  onSaved: () => void;
  notify: (type: 'success' | 'error' | 'info', msg: string) => void;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [triggerType, setTriggerType] = useState<AutomationTrigger>('cron');
  const [cronExpression, setCronExpression] = useState('0 9 * * *');
  const [eventType, setEventType] = useState('');
  const [promptTemplate, setPromptTemplate] = useState('');
  const [toolsAllowed, setToolsAllowed] = useState<string[]>([]);
  const [model, setModel] = useState('');
  const [maxIterations, setMaxIterations] = useState(5);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!!automationId);

  // Available tools for picker
  const [availableTools, setAvailableTools] = useState<MCPTool[]>([]);
  const [toolSearch, setToolSearch] = useState('');
  const [showToolPicker, setShowToolPicker] = useState(false);

  // Load automation if editing
  useEffect(() => {
    if (!automationId) return;
    (async () => {
      try {
        const a = await getAutomation(automationId);
        setName(a.name);
        setDescription(a.description || '');
        setTriggerType(a.trigger_type);
        setCronExpression(a.cron_expression || '');
        setEventType(a.event_type || '');
        setPromptTemplate(a.prompt_template);
        setToolsAllowed(a.tools_allowed || []);
        setModel(a.model);
        setMaxIterations(a.max_iterations);
      } catch (e: any) { notify('error', e.message); onClose(); }
      finally { setLoading(false); }
    })();
  }, [automationId]);

  // Load available tools
  useEffect(() => {
    getMCPTools().then(setAvailableTools).catch(() => {});
  }, []);

  const handleSave = async () => {
    if (!name.trim() || !promptTemplate.trim()) {
      notify('error', 'Nombre y prompt template son requeridos');
      return;
    }
    setSaving(true);
    try {
      const body = {
        name: name.trim(),
        description: description.trim(),
        trigger_type: triggerType,
        cron_expression: triggerType === 'cron' ? cronExpression : null,
        event_type: triggerType === 'event' ? eventType : null,
        prompt_template: promptTemplate,
        tools_allowed: toolsAllowed,
        model: model || null,
        max_iterations: maxIterations,
      };
      if (automationId) {
        await updateAutomation(automationId, body);
        notify('success', 'Automatización actualizada');
      } else {
        await createAutomation(body as any);
        notify('success', 'Automatización creada');
      }
      onSaved();
    } catch (e: any) { notify('error', e.message); }
    finally { setSaving(false); }
  };

  const toggleTool = (toolName: string) => {
    setToolsAllowed(prev =>
      prev.includes(toolName) ? prev.filter(t => t !== toolName) : [...prev, toolName]
    );
  };

  const filteredTools = availableTools.filter(t =>
    !toolSearch.trim() || t.name.toLowerCase().includes(toolSearch.toLowerCase()) || t.description?.toLowerCase().includes(toolSearch.toLowerCase())
  );

  // Close on Escape
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  if (loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center" style={{ zIndex: 60, background: 'rgba(0,0,0,.7)' }}>
        <Loader size={24} className="animate-spin text-white/40" />
      </div>
    );
  }

  return (
    <div className="app-safe-full fixed inset-0 flex flex-col" style={{ zIndex: 60, background: 'var(--bg-dark)' }}>
      {/* Header */}
      <div className="shrink-0 flex items-center justify-between px-6 py-3 border-b border-white/10"
           style={{ background: 'linear-gradient(180deg,rgba(255,255,255,.08),rgba(255,255,255,.03))' }}>
        <h3 className="text-sm font-bold text-white flex items-center gap-2">
          <Zap size={16} style={{ color: 'var(--acc2)' }} />
          {automationId ? 'Editar automatización' : 'Nueva automatización'}
        </h3>
        <button onClick={onClose} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-white/40 hover:text-white hover:bg-white/10 transition-colors text-xs">
          <X size={16} />Cerrar
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-2xl mx-auto px-6 py-6 space-y-5 pb-24">

          {/* Name & Description */}
          <div className="o-card space-y-4">
            <Field label="Nombre">
              <Input value={name} onChange={e => setName(e.target.value)} placeholder="Blog SEO cada 3 días" />
            </Field>
            <Field label="Descripción" hint="opcional">
              <Textarea value={description} onChange={e => setDescription(e.target.value)} rows={2} placeholder="¿Qué hace esta automatización?" />
            </Field>
          </div>

          {/* Trigger */}
          <div className="o-card space-y-4">
            <div className="flex items-center gap-2 mb-1">
              <Clock size={14} style={{ color: 'var(--acc1)' }} />
              <span className="text-sm font-semibold text-white">Trigger</span>
            </div>

            <div className="grid grid-cols-4 gap-2">
              {(['cron', 'event', 'webhook', 'manual'] as AutomationTrigger[]).map(t => (
                <button key={t} onClick={() => setTriggerType(t)}
                  className={`flex flex-col items-center gap-1 px-3 py-2.5 rounded-lg border text-xs transition-all ${
                    triggerType === t
                      ? 'border-[var(--acc1)] bg-[rgba(var(--acc1-rgb),.1)] text-[var(--acc1)]'
                      : 'border-white/10 text-white/40 hover:text-white/60 hover:border-white/20'
                  }`}>
                  {TRIGGER_ICON[t]}
                  <span className="mt-0.5">{TRIGGER_LABEL[t].split(' ')[0]}</span>
                </button>
              ))}
            </div>

            {triggerType === 'cron' && (
              <div className="space-y-3">
                <Field label="Expresión Cron" hint="minuto hora día mes díaSemana">
                  <Input value={cronExpression} onChange={e => setCronExpression(e.target.value)} placeholder="0 9 */3 * *" />
                </Field>
                <div className="flex flex-wrap gap-1.5">
                  {CRON_PRESETS.map(p => (
                    <button key={p.value} onClick={() => setCronExpression(p.value)}
                      className={`px-2 py-1 rounded text-[10px] border transition-colors ${
                        cronExpression === p.value
                          ? 'border-[var(--acc1)] text-[var(--acc1)] bg-[rgba(var(--acc1-rgb),.1)]'
                          : 'border-white/10 text-white/30 hover:text-white/50'
                      }`}>
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {triggerType === 'event' && (
              <Field label="Tipo de evento">
                <Input value={eventType} onChange={e => setEventType(e.target.value)} placeholder="booking_created, lead_captured, review_received" />
              </Field>
            )}
          </div>

          {/* Prompt Template */}
          <div className="o-card space-y-3">
            <div className="flex items-center gap-2 mb-1">
              <FileText size={14} style={{ color: 'var(--acc2)' }} />
              <span className="text-sm font-semibold text-white">Prompt Template</span>
            </div>
            <p className="text-[10px] text-white/25">
              Variables disponibles: <code className="text-white/40">{'{{org_id}}'}</code> <code className="text-white/40">{'{{now}}'}</code> <code className="text-white/40">{'{{last_run}}'}</code>
            </p>
            <Textarea value={promptTemplate} onChange={e => setPromptTemplate(e.target.value)} rows={8}
              placeholder="Revisa los blogs publicados de {{org_id}} y genera un nuevo artículo SEO..." />
            <p className="text-[10px] text-white/20 text-right">{promptTemplate.length.toLocaleString()} / 100,000 caracteres</p>
          </div>

          {/* Model & Iterations */}
          <div className="o-card space-y-4">
            <div className="flex items-center gap-2 mb-1">
              <Settings size={14} style={{ color: 'var(--acc3)' }} />
              <span className="text-sm font-semibold text-white">Configuración del modelo</span>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Modelo">
                <select value={model} onChange={e => setModel(e.target.value)}
                  className="w-full px-3 py-2 text-sm rounded-lg bg-white/5 border border-white/10 text-white outline-none">
                  {MODELS.map(m => <option key={m.value} value={m.value} className="bg-[var(--bg-dark)]">{m.label}</option>)}
                </select>
              </Field>
              <Field label="Máximo de iteraciones" hint="1-20">
                <Input type="number" value={maxIterations}
                  onChange={e => setMaxIterations(Math.max(1, Math.min(20, parseInt(e.target.value) || 5)))} />
              </Field>
            </div>
          </div>

          {/* Tools Selector */}
          <div className="o-card space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Wrench size={14} style={{ color: 'var(--acc1)' }} />
                <span className="text-sm font-semibold text-white">Tools permitidas</span>
              </div>
              <button onClick={() => setShowToolPicker(!showToolPicker)}
                className="text-[10px] text-white/30 hover:text-white/50 transition-colors">
                {showToolPicker ? 'Ocultar' : 'Seleccionar tools'}
              </button>
            </div>

            {toolsAllowed.length === 0 && (
              <p className="text-[10px] text-white/20">Sin restricción — puede usar todas las tools disponibles</p>
            )}

            {toolsAllowed.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {toolsAllowed.map(t => (
                  <span key={t} className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] bg-[rgba(var(--acc1-rgb),.1)] text-[var(--acc1)] border border-[rgba(var(--acc1-rgb),.2)]">
                    {t}
                    <button onClick={() => toggleTool(t)} className="hover:text-red-400 transition-colors"><X size={10} /></button>
                  </span>
                ))}
              </div>
            )}

            {showToolPicker && (
              <div className="border-t border-white/[.06] pt-3 space-y-2">
                <div className="relative">
                  <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-white/20" />
                  <input value={toolSearch} onChange={e => setToolSearch(e.target.value)} placeholder="Buscar tool…"
                    className="w-full pl-7 pr-3 py-1.5 text-[11px] rounded-lg bg-white/[.03] border border-white/10 text-white placeholder:text-white/20 outline-none" />
                </div>
                <div className="max-h-[200px] overflow-y-auto space-y-0.5">
                  {filteredTools.map(t => (
                    <button key={t.tool_id} onClick={() => toggleTool(t.name)}
                      className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left text-[11px] transition-colors ${
                        toolsAllowed.includes(t.name)
                          ? 'bg-[rgba(var(--acc1-rgb),.08)] text-[var(--acc1)]'
                          : 'text-white/40 hover:bg-white/[.03] hover:text-white/60'
                      }`}>
                      <CheckCircle2 size={12} className={toolsAllowed.includes(t.name) ? 'opacity-100' : 'opacity-20'} />
                      <span className="font-mono">{t.name}</span>
                      {t.description && <span className="text-white/20 truncate ml-auto text-[10px]">{t.description}</span>}
                    </button>
                  ))}
                  {filteredTools.length === 0 && <p className="text-center text-white/15 text-[10px] py-3">Sin resultados</p>}
                </div>
              </div>
            )}
          </div>

          {/* Save */}
          <div className="flex justify-end gap-3">
            <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-white/40 hover:text-white/60 transition-colors">Cancelar</button>
            <SaveBtn onClick={handleSave} saving={saving} label={automationId ? 'Guardar cambios' : 'Crear automatización'} disabled={!name.trim() || !promptTemplate.trim()} />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   HISTORY DRAWER
   ═══════════════════════════════════════════════════════════════ */

function HistoryDrawer({ automationId, automationName, onClose, notify }: {
  automationId: string;
  automationName: string;
  onClose: () => void;
  notify: (type: 'success' | 'error' | 'info', msg: string) => void;
}) {
  const [runs, setRuns] = useState<AutomationRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedRun, setExpandedRun] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await listAutomationRuns(automationId, { limit: 30 });
        setRuns(data.runs || []);
      } catch (e: any) { notify('error', e.message); }
      finally { setLoading(false); }
    })();
  }, [automationId]);

  // Close on Escape
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  return (
    <div className="fixed inset-y-0 right-0 w-full sm:w-[480px] flex flex-col border-l border-white/10"
         style={{ zIndex: 60, background: 'var(--bg-dark)', backdropFilter: 'blur(20px)' }}>
      {/* Header */}
      <div className="shrink-0 flex items-center justify-between px-5 py-3 border-b border-white/10">
        <div>
          <h3 className="text-sm font-bold text-white flex items-center gap-2">
            <FileText size={14} style={{ color: 'var(--acc2)' }} />
            Historial
          </h3>
          <p className="text-[10px] text-white/30 mt-0.5">{automationName}</p>
        </div>
        <button onClick={onClose} className="p-1.5 rounded-lg text-white/30 hover:text-white hover:bg-white/10 transition-colors">
          <X size={16} />
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
        {loading ? (
          <div className="flex items-center gap-2 py-8 justify-center text-white/30 text-sm">
            <Loader size={16} className="animate-spin" />Cargando…
          </div>
        ) : runs.length === 0 ? (
          <EmptyState icon={<FileText size={24} />} text="Sin ejecuciones aún" />
        ) : (
          runs.map(run => (
            <div key={run.id} className="rounded-xl border border-white/10 bg-white/[.02] overflow-hidden">
              {/* Run header */}
              <button onClick={() => setExpandedRun(expandedRun === run.id ? null : run.id)}
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-white/[.02] transition-colors">
                {/* Status dot */}
                <div className={`w-2 h-2 rounded-full shrink-0 ${
                  run.status === 'success' ? 'bg-emerald-400' :
                  run.status === 'failed' ? 'bg-red-400' :
                  run.status === 'running' ? 'bg-sky-400 animate-pulse' : 'bg-amber-400'
                }`} />

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <Badge variant={RUN_BADGE[run.status] || 'default'}>{RUN_LABEL[run.status] || run.status}</Badge>
                    <span className="text-[10px] text-white/20">
                      {new Date(run.started_at).toLocaleDateString('es', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                  {run.response_summary && (
                    <p className="text-[11px] text-white/30 mt-0.5 truncate">{run.response_summary}</p>
                  )}
                </div>

                <div className="flex items-center gap-3 shrink-0 text-[10px] text-white/20">
                  {run.tokens_used > 0 && <span>{run.tokens_used.toLocaleString()} tokens</span>}
                  {run.finished_at && run.started_at && (
                    <span>{Math.round((new Date(run.finished_at).getTime() - new Date(run.started_at).getTime()) / 1000)}s</span>
                  )}
                  {expandedRun === run.id ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                </div>
              </button>

              {/* Expanded details */}
              {expandedRun === run.id && (
                <div className="px-4 pb-4 space-y-3 border-t border-white/[.06]">
                  {/* Error */}
                  {run.error_message && (
                    <div className="flex items-start gap-2 p-2 rounded-lg bg-red-500/10 border border-red-500/20 mt-3">
                      <AlertCircle size={14} className="text-red-400 shrink-0 mt-0.5" />
                      <p className="text-[11px] text-red-400">{run.error_message}</p>
                    </div>
                  )}

                  {/* Summary */}
                  {run.response_summary && (
                    <div className="mt-3">
                      <span className="text-[10px] text-white/30 uppercase tracking-wider font-semibold">Resumen</span>
                      <p className="text-[11px] text-white/50 mt-1">{run.response_summary}</p>
                    </div>
                  )}

                  {/* Tool calls */}
                  {run.tool_calls && run.tool_calls.length > 0 && (
                    <div>
                      <span className="text-[10px] text-white/30 uppercase tracking-wider font-semibold">Tools ejecutadas ({run.tool_calls.length})</span>
                      <div className="mt-1 space-y-1">
                        {run.tool_calls.map((tc: any, i: number) => (
                          <div key={i} className="flex items-center gap-2 px-2 py-1 rounded bg-white/[.03] text-[10px]">
                            <Wrench size={10} className="text-white/20 shrink-0" />
                            <span className="font-mono text-white/40">{tc.name || tc.tool || JSON.stringify(tc).slice(0, 60)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Prompt sent */}
                  {run.prompt_sent && (
                    <details className="group">
                      <summary className="text-[10px] text-white/30 uppercase tracking-wider font-semibold cursor-pointer hover:text-white/40">
                        Prompt enviado
                      </summary>
                      <pre className="mt-1 p-2 rounded-lg bg-white/[.03] border border-white/[.06] text-white/30 text-[10px] font-mono whitespace-pre-wrap max-h-[200px] overflow-y-auto">
                        {run.prompt_sent}
                      </pre>
                    </details>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default AutomationsPage;
